// src/context/ThemeContext.jsx
//
// App-wide light/dark theme. Persists to localStorage under "nd-theme" and
// reflects the choice as data-theme="light|dark" on <html>, which drives the
// CSS variables in src/styles/theme.css. Default is "dark" so existing screens
// are unchanged until a user flips the toggle.
//
// To avoid a flash, the initial attribute is also set synchronously in index.js
// before React renders; this provider keeps it in sync afterwards.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'nd-theme';

const readInitial = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (_) { /* localStorage blocked — fall through */ }
  return 'dark';
};

const ThemeContext = createContext({ theme: 'dark', toggleTheme: () => {}, setTheme: () => {} });

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readInitial);

  // Apply to <html> + persist whenever it changes.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch (_) { /* ignore */ }
    // Enable the cross-fade transition only after the first apply, so the very
    // first paint doesn't animate from a wrong color.
    const id = window.requestAnimationFrame(() => root.classList.add('nd-theme-anim'));
    return () => window.cancelAnimationFrame(id);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
